import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const getMonthString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const LineLegend = ({ items }) => (
  <div className="line-legend" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
    {items.map((it) => (
      <div key={it.label} className="line-legend-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg className="line-legend-swatch" width="24" height="6" viewBox="0 0 24 6" aria-hidden="true">
          <line x1="0" y1="3" x2="24" y2="3" stroke={it.color} strokeWidth="3" strokeDasharray={it.dashed ? '6 3' : undefined} />
        </svg>
        <span className="line-legend-label" style={{ fontSize: 11, color: '#555' }}>{it.label}</span>
      </div>
    ))}
  </div>
);

const ExchangeEmailStatsChart = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dailySplit, setDailySplit] = useState(null);

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  // Calculate email hygiene health score
  const healthScore = useMemo(() => {
    if (!analytics?.previous) return null;
    const readRate = analytics.previous.totalReceived > 0
      ? (analytics.previous.readCount / analytics.previous.totalReceived) * 100
      : 0;
    return Math.round(readRate);
  }, [analytics]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 70) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 50) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 30) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 15) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Poor', color: '#d32f2f', icon: '✗' };
  };

  useEffect(() => { fetchAllPeriods(); }, []); // eslint-disable-line

  async function fetchMonthStats(monthStr) {
    const response = await apiFetch(`/api/exchange/email-stats?month=${monthStr}`);
    if (!response.ok) throw new Error('Failed to fetch email stats');
    return await response.json();
  }
  async function fetchAnalyticsCompare() { const r = await apiFetch('/api/exchange/email-analytics/compare'); if (!r.ok) throw new Error('Failed to fetch email analytics'); return await r.json(); }
  async function fetchDailySplit() { const r = await apiFetch('/api/exchange/email-daily-split'); if (!r.ok) throw new Error('Failed to fetch daily split'); return await r.json(); }

  async function fetchAllPeriods() {
    setLoading(true); setError('');
    try {
      const now = new Date(); let year = now.getFullYear(); let month = now.getMonth() + 1;
      let prevMonth = month - 1; let prevYear = year; if (prevMonth === 0) { prevMonth = 12; prevYear -= 1; }
      let beforePrevMonth = prevMonth - 1; let beforePrevYear = prevYear; if (beforePrevMonth === 0) { beforePrevMonth = 12; beforePrevYear -= 1; }
      const prevDate = new Date(prevYear, prevMonth - 1); const beforePrevDate = new Date(beforePrevYear, beforePrevMonth - 1);
      const [previous, beforePrevious, analyticsCompare, split] = await Promise.all([
        fetchMonthStats(getMonthString(prevDate)),
        fetchMonthStats(getMonthString(beforePrevDate)),
        fetchAnalyticsCompare(),
        fetchDailySplit()
      ]);

      function aggregateByDay(stats, year, month) {
        const days = getDaysInMonth(year, month); const daily = Array.from({ length: days }, (_, i) => ({ day: i + 1, sent: 0, received: 0 }));
        for (const user of stats) (user.dailyStats||[]).forEach(ds => { if (ds.day>=1&&ds.day<=days){ daily[ds.day-1].sent += ds.sent; daily[ds.day-1].received += ds.received; }});
        return daily;
      }
      const prevDaily = previous.some(u=>u.dailyStats) ? aggregateByDay(previous, prevYear, prevMonth) : [];
      const beforePrevDaily = beforePrevious.some(u=>u.dailyStats) ? aggregateByDay(beforePrevious, beforePrevYear, beforePrevMonth) : [];
      const maxDays = Math.max(prevDaily.length, beforePrevDaily.length, 31);
      const merged = Array.from({ length: maxDays }, (_, idx) => ({
        day: idx + 1,
        Sent_Current: prevDaily[idx]?.sent || 0,
        Received_Current: prevDaily[idx]?.received || 0,
        Sent_Previous: beforePrevDaily[idx]?.sent || 0,
        Received_Previous: beforePrevDaily[idx]?.received || 0
      }));
      if (split?.days?.length) split.days.forEach(d => { const i = d.day - 1; if (i>=0 && i<merged.length){ merged[i].Sent_Internal = d.sentInternal||0; merged[i].Sent_External = d.sentExternal||0; merged[i].Received_Internal = d.receivedInternal||0; merged[i].Received_External = d.receivedExternal||0; }});
      setChartData(merged);

      // Active user counts (filter by valid email field)
      const userCountPrior = beforePrevious.filter(u => u.email).length;
      const userCountPrev = previous.filter(u => u.email).length;
      const userCountChangePct = userCountPrior === 0 ? 0 : ((userCountPrev - userCountPrior) / userCountPrior) * 100;

      const sum = arr => arr.reduce((a,b)=>a+(b||0),0);
      const totalSent = [sum(beforePrevDaily.map(d=>d.sent)), sum(prevDaily.map(d=>d.sent))];
      const totalReceived = [sum(beforePrevDaily.map(d=>d.received)), sum(prevDaily.map(d=>d.received))];
      const sentChange = totalSent[0]===0?0:((totalSent[1]-totalSent[0])/totalSent[0])*100;
      const receivedChange = totalReceived[0]===0?0:((totalReceived[1]-totalReceived[0])/totalReceived[0])*100;
      const beforePrevLabel = beforePrevDate.toLocaleDateString(undefined,{month:'long',year:'numeric'});
      const prevLabel = prevDate.toLocaleDateString(undefined,{month:'long',year:'numeric'});
      setSummary({ totalSent, totalReceived, sentChange, receivedChange, months:[beforePrevLabel, prevLabel], userCounts:{ prior:userCountPrior, prev:userCountPrev, changePct:userCountChangePct } });
      setAnalytics(analyticsCompare); setDailySplit(split);
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setChartData([]); setSummary(null); setAnalytics(null); setDailySplit(null);
    }
    finally { setLoading(false); }
  }

  const formatNumber = (n) => n?.toLocaleString('en-US').replace(/,/g, ' ');
  const InlineIndicator = ({ value }) => { if (value===0) return null; const isUp = value>0; const color = isUp?'green':'red'; const arrow = isUp?"\u2191":"\u2193"; return <span style={{ color, fontWeight:600, marginLeft:4 }}>{arrow}{Math.abs(value).toFixed(1)}%</span>; };
  const CustomTooltip = ({ active, payload, label }) => { if (!active || !payload?.length) return null; return (<div style={{ background:'#fff', border:'1px solid #ccc', padding:8, borderRadius:4, fontSize:11 }}><div style={{ fontWeight:600, marginBottom:2 }}>Day {label}</div>{payload.map((p,i)=>(<div key={i} style={{ color:p.color }}>{p.name}: {formatNumber(p.value)}</div>))}</div>); };

  if (loading) return <HexagonLoader />; 
  if (error) return <div style={{ color:'red' }}>{error}</div>;

  const prevLabel = summary?.months?.[1]; 
  const priorLabel = summary?.months?.[0];
  const scoreInterp = getScoreInterpretation(healthScore);
  const readRate = analytics?.previous?.totalReceived > 0 ? (analytics.previous.readCount / analytics.previous.totalReceived) * 100 : 0;
  const totalSent = summary?.totalSent?.[1] || 0;
  const totalReceived = summary?.totalReceived?.[1] || 0;
  const activeUsers = summary?.userCounts?.prev || 0;
  const daysInMonth = chartData.length;
  const avgEmailsPerUserPerDay = (activeUsers > 0 && daysInMonth > 0) ? (totalSent + totalReceived) / activeUsers / daysInMonth : 0;

  const llmProxyText = [`Email Hygiene Score: ${healthScore}/100`,`Read Rate: ${readRate.toFixed(1)}%`,`Total Sent: ${formatNumber(totalSent)}`,`Total Received: ${formatNumber(totalReceived)}`,`Active Users: ${formatNumber(activeUsers)}`].join('\n');

  return (
    <div className="dashboard-card group-wrapper exchange-email-stats-chart">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom: 12 }}>Email Communication & Engagement</h2>

      {/* Executive Summary Banner */}
      {analytics?.previous && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
                {healthScore != null ? `${healthScore}%` : '—'}
              </div>
              <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
              <div className="exec-banner-sublabel">Email Read Rate ({prevLabel})</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> Your organization processed <strong>{formatNumber(totalSent + totalReceived)} emails</strong> in {prevLabel} ({formatNumber(activeUsers)} active users) with <strong>{readRate.toFixed(0)}% read rate</strong>.
                {readRate >= 70 && ' Excellent email engagement - users reading messages.'}
                {readRate >= 50 && readRate < 70 && ' Good email hygiene.'}
                {readRate < 50 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Low read rate suggests email overload.</span>}
                {avgEmailsPerUserPerDay > 150 && <span style={{ color: '#ffa000', fontWeight: 600 }}> High volume ({avgEmailsPerUserPerDay.toFixed(0)}/user/day).</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {summary && analytics?.previous && (
        <div className="metrics-row">
          <div className="dashboard-card metric-card" title="Emails sent by organization users">
            <div className="metric-label">Emails Sent</div>
            <div className="metric-value--md" style={{ fontWeight:700 }}>{formatNumber(totalSent)}</div>
            {summary.sentChange !== 0 && <div className="metric-delta"><InlineIndicator value={summary.sentChange} /></div>}
          </div>
          <div className="dashboard-card metric-card" title="Emails received by organization users">
            <div className="metric-label">Emails Received</div>
            <div className="metric-value--md" style={{ fontWeight:700 }}>{formatNumber(totalReceived)}</div>
            {summary.receivedChange !== 0 && <div className="metric-delta"><InlineIndicator value={summary.receivedChange} /></div>}
          </div>
          <div className="dashboard-card metric-card" title="Users who sent or received email">
            <div className="metric-label">Active Users</div>
            <div className="metric-value--md" style={{ fontWeight:700 }}>{formatNumber(activeUsers)}</div>
            {summary.userCounts?.changePct !== 0 && <div className="metric-delta"><InlineIndicator value={summary.userCounts.changePct} /></div>}
          </div>
          <div className="dashboard-card metric-card" title="Percentage of emails opened/read">
            <div className="metric-label">Read Rate</div>
            <div className="metric-value--md" style={{ fontWeight:700, color: readRate >= 70 ? '#2e7d32' : readRate >= 50 ? '#7cb342' : '#d32f2f' }}>
              {readRate.toFixed(0)}%
            </div>
            <div className="metric-subtitle" style={{ marginTop: 4 }}>{formatNumber(analytics.previous.readCount)} read</div>
          </div>
        </div>
      )}

      {/* Month Comparison Table */}
      {summary && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Month-over-Month Activity</h4>
          <div className="chart-subtitle">Email volume trends</div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '6px' }}>Month</th>
                <th style={{ padding: '6px' }}>Sent</th>
                <th style={{ padding: '6px' }}>Received</th>
                <th style={{ padding: '6px' }}>Active Users</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #eee' }}>
                <td style={{ textAlign:'left', fontWeight:600, padding: '6px' }}>{priorLabel}</td>
                <td style={{ padding: '6px' }}>{formatNumber(summary.totalSent[0])}</td>
                <td style={{ padding: '6px' }}>{formatNumber(summary.totalReceived[0])}</td>
                <td style={{ padding: '6px' }}>{formatNumber(summary.userCounts?.prior)}</td>
              </tr>
              <tr style={{ borderTop: '1px solid #eee' }}>
                <td style={{ textAlign:'left', fontWeight:600, padding: '6px' }}>{prevLabel}</td>
                <td style={{ padding: '6px', fontWeight: 600 }}>{formatNumber(summary.totalSent[1])}<InlineIndicator value={summary.sentChange} /></td>
                <td style={{ padding: '6px', fontWeight: 600 }}>{formatNumber(summary.totalReceived[1])}<InlineIndicator value={summary.receivedChange} /></td>
                <td style={{ padding: '6px', fontWeight: 600 }}>{formatNumber(summary.userCounts?.prev)}<InlineIndicator value={summary.userCounts?.changePct || 0} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Analytics Split */}
       {analytics && (
         <div className="dashboard-grid compact">
           <div className="metric-card metric-card--lg" title="Internal vs External emails sent">
            <div className="metric-label">Sent: Internal / External</div>
            <div className="metric-value">{formatNumber(analytics.previous.sentInternal)} / {formatNumber(analytics.previous.sentExternal)}</div>
            <div className="metric-subtitle">
              {((analytics.previous.sentInternal / (analytics.previous.sentInternal + analytics.previous.sentExternal)) * 100).toFixed(0)}% internal
            </div>
          </div>
           <div className="metric-card metric-card--lg" title="Internal vs External emails received">
            <div className="metric-label">Received: Internal / External</div>
            <div className="metric-value">{formatNumber(analytics.previous.receivedInternal)} / {formatNumber(analytics.previous.receivedExternal)}</div>
            <div className="metric-subtitle">
              {((analytics.previous.receivedExternal / (analytics.previous.receivedInternal + analytics.previous.receivedExternal)) * 100).toFixed(0)}% external
            </div>
          </div>
        </div>
      )}

      {/* Top Senders/Recipients */}
      {analytics?.previous && (
        <div className="dashboard-grid compact" style={{ marginTop:12 }}>
          {['topSendersInternal','topSendersExternal','topRecipientsInternal','topRecipientsExternal'].map(key => {
            const titleMap = {
              topSendersInternal: 'Top Senders — Internal',
              topSendersExternal: 'Top Senders — External',
              topRecipientsInternal: 'Top Recipients — Internal',
              topRecipientsExternal: 'Top Recipients — External'
            };
            return (
              <div key={key} className="dashboard-card chart-card">
                <h4 className="chart-title">{titleMap[key]}</h4>
                <div className="chart-subtitle">Most active ({prevLabel})</div>
                <table className="two-col-header" style={{ width:'100%', marginBottom:0, fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', backgroundColor: primaryColor, padding: '4px 6px' }}>User</th>
                      <th style={{ width:70, backgroundColor: secondaryColor, padding: '4px 6px' }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.previous[key]||[]).slice(0,5).map((u,idx)=>(
                      <tr key={u.email||idx} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ textAlign:'left', padding: '4px 6px', fontWeight: 500 }} title={u.email}>{u.name||u.email}</td>
                        <td style={{ textAlign:'right', fontWeight:600, padding: '4px 6px' }}>{formatNumber(u.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* Daily Volume Chart */}
      <div className="dashboard-card chart-card">
        <h4 className="chart-title">Daily Email Volume Comparison</h4>
        <div className="chart-subtitle">Sent vs Received trends ({prevLabel} vs {priorLabel})</div>
        <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={180} minHeight={180}>
          <LineChart data={chartData} margin={{ top:4, right:8, left:0, bottom:4 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }} />
            <YAxis tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<LineLegend items={[
              { label: `Recv (${prevLabel})`, color: secondaryColor },
              { label: `Recv (${priorLabel})`, color: secondaryColor, dashed:true },
              { label: `Sent (${prevLabel})`, color: primaryColor },
              { label: `Sent (${priorLabel})`, color: primaryColor, dashed:true }
            ]} />} />
            <Line type="monotone" dataKey="Sent_Current" name={`Sent (${prevLabel})`} stroke={primaryColor} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
            <Line type="monotone" dataKey="Received_Current" name={`Received (${prevLabel})`} stroke={secondaryColor} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
            <Line type="monotone" dataKey="Sent_Previous" name={`Sent (${priorLabel})`} stroke={primaryColor} strokeDasharray="6 3" dot={false} isAnimationActive={!isPrintMode} />
            <Line type="monotone" dataKey="Received_Previous" name={`Received (${priorLabel})`} stroke={secondaryColor} strokeDasharray="6 3" dot={false} isAnimationActive={!isPrintMode} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Internal vs External Split */}
      {dailySplit && (
        <div className="dashboard-grid compact">
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Sent: Internal vs External ({prevLabel})</h4>
            <div className="chart-subtitle">Organization vs external communications</div>
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={formatNumber} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<LineLegend items={[
                  { label: 'Sent External', color: primaryColor },
                  { label: 'Sent Internal', color: primaryColor, dashed:true }
                ]} />} />
                <Line type="monotone" dataKey="Sent_External" name="Sent External" stroke={primaryColor} dot={false} isAnimationActive={!isPrintMode} />
                <Line type="monotone" dataKey="Sent_Internal" name="Sent Internal" stroke={primaryColor} strokeDasharray="6 3" dot={false} isAnimationActive={!isPrintMode} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Received: Internal vs External ({prevLabel})</h4>
            <div className="chart-subtitle">Incoming from organization vs external sources</div>
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={150} minHeight={150}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={formatNumber} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<LineLegend items={[
                  { label: 'Recv External', color: secondaryColor },
                  { label: 'Recv Internal', color: secondaryColor, dashed:true }
                ]} />} />
                <Line type="monotone" dataKey="Received_External" name="Recv External" stroke={secondaryColor} dot={false} isAnimationActive={!isPrintMode} />
                <Line type="monotone" dataKey="Received_Internal" name="Recv Internal" stroke={secondaryColor} strokeDasharray="6 3" dot={false} isAnimationActive={!isPrintMode} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Email Metrics:</strong>
        <ul>
          <li><strong>Email Hygiene Score:</strong> Read rate percentage. {'>'}70% = Excellent engagement. Low rates suggest overload or irrelevant distributions.</li>
          <li><strong>Read Rate:</strong> % of received emails users opened. Indicates relevance and inbox manageability.</li>
          <li><strong>Emails Per User:</strong> {avgEmailsPerUserPerDay.toFixed(0)}/day average. {'>'}150/day may need optimization via Teams or distribution list cleanup.</li>
          <li><strong>Internal vs External:</strong> Organization emails vs external. High external may indicate customer-facing roles or spam issues.</li>
          <li><strong>Top Users:</strong> Power users or automated systems - optimization opportunities for distribution lists.</li>
        </ul>
        {readRate < 50 && (
          <div className="action-callout">
            <strong>Email Overload:</strong> {readRate.toFixed(0)}% read rate suggests overwhelmed users. Consider: consolidating distribution lists, using Teams for quick messages, email-free hours, or inbox management training.
          </div>
        )}
        {avgEmailsPerUserPerDay > 150 && (
          <div className="action-callout">
            <strong>High Volume:</strong> {avgEmailsPerUserPerDay.toFixed(0)} emails/user/day is high. Evaluate: reducing automated notifications, narrowing distribution lists, using instant messaging for quick questions.
          </div>
        )}
      </div>

      <div className="data-source">
        Data Source: Exchange Online mailbox statistics (previous 2 months). Read count based on MessageRead events. Active users = sent or received at least one email during period.
      </div>
    </div>
  );
};

export default ExchangeEmailStatsChart;
